import { ApiError } from '@sw/api-client';

/**
 * Error reporter del Client Portal.
 *
 * En dev: console.error con info estructurada (no envía nada al backend).
 * En prod: POST batched + dedup a `/api/errors/client-portal`. Patrón espejo del
 * Chrome Extension reporter del backend (`POST /api/errors/extension`), que mete los
 * errores en el pipeline NLog que envía email automáticamente a ivan.jordan@codingband.com.
 *
 * Backend endpoint pendiente de implementación — ticket SW-3xx separado. Mientras tanto,
 * en prod los errores van a console igual (con flag `__not_reported_yet`) para no perderlos.
 *
 * Convenciones:
 * - Dedup por `${context}|${message}|${stack?.split('\n')[0]}` con TTL 60s — evita flood.
 * - Batch hasta 20 errores o flush cada 10s, lo que ocurra primero.
 * - Beacon API en `beforeunload` para no perder errores en page exit.
 */

export type ReportableError = {
  context: string;
  message: string;
  stack?: string;
  url: string;
  userAgent: string;
  timestamp: string;
  count: number;
  apiStatus?: number;
  apiBody?: unknown;
};

type DedupEntry = { hash: string; firstSeen: number; lastError: ReportableError };

const DEDUP_TTL_MS = 60_000;
const BATCH_MAX = 20;
const BATCH_FLUSH_MS = 10_000;

class ErrorReporter {
  private queue: ReportableError[] = [];
  private dedup = new Map<string, DedupEntry>();
  private flushTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly endpoint: string;
  private readonly devMode: boolean;

  constructor(endpoint: string, devMode: boolean) {
    this.endpoint = endpoint;
    this.devMode = devMode;
  }

  /**
   * Captura un error. Maneja dedup, batching y envío.
   */
  report(error: unknown, context: string): void {
    const reportable = this.toReportable(error, context);
    const hash = this.hash(reportable);
    const now = Date.now();

    // Dedup window
    const existing = this.dedup.get(hash);
    if (existing && now - existing.firstSeen < DEDUP_TTL_MS) {
      existing.lastError.count += 1;
      return;
    }

    this.dedup.set(hash, { hash, firstSeen: now, lastError: reportable });
    this.queue.push(reportable);

    if (this.devMode) {
      // En dev no batched — log inmediato al console para feedback rápido del dev.
      // eslint-disable-next-line no-console
      console.error('[client-portal:error]', reportable);
      return;
    }

    if (this.queue.length >= BATCH_MAX) {
      void this.flush();
    } else {
      this.scheduleFlush();
    }
  }

  /**
   * Llamado en beforeunload para no perder errores en page exit. Usa Beacon API
   * (sincrónico, sobrevive al unload del documento).
   */
  flushOnUnload(): void {
    if (this.queue.length === 0 || this.devMode) return;
    const payload = JSON.stringify({ errors: this.queue });
    this.queue = [];
    try {
      if (typeof navigator !== 'undefined' && 'sendBeacon' in navigator) {
        navigator.sendBeacon(this.endpoint, payload);
      }
    } catch {
      // best effort — ya estamos en unload, no podemos hacer más
    }
  }

  private scheduleFlush(): void {
    if (this.flushTimer) return;
    this.flushTimer = setTimeout(() => {
      this.flushTimer = null;
      void this.flush();
    }, BATCH_FLUSH_MS);
  }

  private async flush(): Promise<void> {
    if (this.queue.length === 0) return;
    const batch = this.queue.splice(0, BATCH_MAX);
    if (this.flushTimer) {
      clearTimeout(this.flushTimer);
      this.flushTimer = null;
    }

    try {
      // No usamos el http client porque queremos evitar loop si el http client lanza errores
      // (ej. el CSRF wrapper falla y dispara reporter, que vuelve a hacer fetch, que falla...).
      await fetch(this.endpoint, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ errors: batch }),
      });
    } catch {
      // Sin reintento — si el backend de errores está caído, dropeamos. Mejor que loop infinito.
      // Los errores quedan en console igual via globalHandlers en dev.
    }
  }

  private toReportable(error: unknown, context: string): ReportableError {
    const base: ReportableError = {
      context,
      message: 'Unknown error',
      url: typeof window !== 'undefined' ? window.location.href : '',
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
      timestamp: new Date().toISOString(),
      count: 1,
    };

    if (error instanceof ApiError) {
      return {
        ...base,
        message: error.message,
        stack: error.stack,
        apiStatus: error.status,
        apiBody: error.body,
      };
    }
    if (error instanceof Error) {
      return { ...base, message: error.message, stack: error.stack };
    }
    if (typeof error === 'string') {
      return { ...base, message: error };
    }
    return { ...base, message: String(error) };
  }

  private hash(r: ReportableError): string {
    const stackHead = r.stack?.split('\n')[0] ?? '';
    return `${r.context}|${r.message}|${stackHead}`;
  }
}

/**
 * Singleton del reporter. Inicializado al boot con env + endpoint.
 */
let _reporter: ErrorReporter | null = null;

export function initErrorReporter(): ErrorReporter {
  if (_reporter) return _reporter;
  const apiBase = import.meta.env.VITE_API_BASE_URL ?? '';
  const endpoint = `${apiBase.replace(/\/$/, '')}/api/errors/client-portal`;
  const devMode = import.meta.env.DEV;
  _reporter = new ErrorReporter(endpoint, devMode);
  return _reporter;
}

export function reportError(error: unknown, context: string): void {
  const reporter = _reporter ?? initErrorReporter();
  reporter.report(error, context);
}

export function flushErrorsOnUnload(): void {
  _reporter?.flushOnUnload();
}
